import { useEffect, useState } from "react";
import { Users2, GitBranch, Banknote, Copy, Share2, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";

function fmt(v) { return Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 }); }

export default function PromoDownline() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [share, setShare] = useState({ promo_code: "", share_link: "" });
  const [overridePct, setOverridePct] = useState(5);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [subRes, linkRes, cfgRes] = await Promise.all([
        api.get("/aff-panel/sub-affiliates"),
        api.get("/aff-panel/sub-affiliates/share-link"),
        api.get("/aff-panel/dashboard").catch(() => ({ data: {} })),
      ]);
      setItems(subRes.data?.items || []);
      setTotal(subRes.data?.total_override_earned || 0);
      setShare(linkRes.data || {});
      // Pull current sub-affiliate %
      // dashboard endpoint exposes commission_pct (parent's); we use settings via separate fetch
      try {
        const cfg = await api.get("/aff-panel/sub-affiliates/config-readonly").catch(() => null);
        if (cfg?.data?.sub_affiliate_pct) setOverridePct(cfg.data.sub_affiliate_pct);
      } catch (_) { /* ignore */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = (t) => navigator.clipboard.writeText(t || "").then(() => toast.success("Copied"));

  return (
    <div data-testid="downline-page">
      <PageHeader
        eyebrow="SUB-AFFILIATES"
        title="My Downline"
        subtitle={`Invite other affiliates with your link — you earn ${overridePct}% override on their commissions.`}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <StatCard label="Sub-Affiliates" value={items.length} icon={Users2} accent="gold" />
        <StatCard label="Override Rate" value={`${overridePct}%`} icon={GitBranch} accent="emerald" raw />
        <StatCard label="Lifetime Override" value={`৳${fmt(total)}`} icon={Banknote} accent="emerald" raw />
      </div>

      {/* Share link card */}
      <div className="p-glass p-5 mb-6">
        <div className="flex items-center gap-2 text-emerald-200 mb-2"><Share2 size={14} />
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold">Your Sub-Affiliate Invite Link</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <code className="flex-1 min-w-[200px] px-3 py-2.5 rounded-lg text-[12.5px] text-white truncate"
                style={{ background: "rgba(0,0,0,0.4)", border: "1px solid rgba(34,192,115,0.25)" }}
                data-testid="share-link">
            {share.share_link || "—"}
          </code>
          <button
            onClick={() => copy(share.share_link)}
            className="p-btn-gold inline-flex items-center gap-1.5 px-3 h-10 rounded-xl text-[12px] font-bold"
            data-testid="share-link-copy"
          >
            <Copy size={12} /> Copy
          </button>
        </div>
        <div className="mt-2 text-[11.5px] text-white/55 flex items-center gap-1">
          <Link2 size={11} /> When someone signs up with your promo code AND applies to become an affiliate, they become your sub-affiliate.
        </div>
      </div>

      {/* List */}
      <div className="p-glass overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead><tr style={{ background: "rgba(255,255,255,0.04)", color: "#a7f3d0" }}>
            <th className="text-left p-3">Sub-Affiliate</th>
            <th className="text-left p-3">Promo Code</th>
            <th className="text-center p-3">Status</th>
            <th className="text-right p-3">Wallet</th>
            <th className="text-right p-3">Override Earned</th>
            <th className="text-right p-3">Joined</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-white/55">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-white/55">No sub-affiliates yet. Share your invite link!</td></tr>
            ) : items.map((s) => (
              <tr key={s.id} className="border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <td className="p-3 text-white">{s.username || "—"}<div className="text-[10.5px] text-white/45">{s.email || ""}</div></td>
                <td className="p-3"><code className="text-[11.5px] px-1.5 py-0.5 rounded" style={{ background: "rgba(34,192,115,0.1)", color: "#a7f3d0" }}>{s.promo_code || "—"}</code></td>
                <td className="p-3 text-center">
                  <span className="px-2 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-[0.18em]"
                    style={{ color: s.status === "active" ? "#a7f3d0" : "#fca5a5",
                             background: s.status === "active" ? "rgba(34,192,115,0.12)" : "rgba(229,75,89,0.12)",
                             border: `1px solid ${s.status === "active" ? "rgba(34,192,115,0.4)" : "rgba(229,75,89,0.4)"}` }}>
                    {s.status || "—"}
                  </span>
                </td>
                <td className="p-3 text-right text-white/85">৳{fmt(s.wallet_balance)}</td>
                <td className="p-3 text-right font-black" style={{ color: "#d4af37" }}>৳{fmt(s.override_earned)}</td>
                <td className="p-3 text-right text-white/55 text-[11.5px]">{(s.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
